import React, { useState, useEffect } from 'react';
import axios from 'axios';
import styles from './OffseasonNotice.module.css';

interface OffseasonNoticeProps {
  feature?: string; // e.g., "battles", "tournaments", "guild leagues"
  children?: React.ReactNode;
  showContentWhenOffseason?: boolean;
}

interface SeasonInfo {
  id: number;
  name: string;
  status: string;
  startDate: string;
}

const OffseasonNotice: React.FC<OffseasonNoticeProps> = ({ feature = 'this feature', children, showContentWhenOffseason = false }) => {
  const [isOffseason, setIsOffseason] = useState<boolean>(false);
  const [nextSeason, setNextSeason] = useState<SeasonInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkSeason = async () => {
      try {
        const response = await axios.get('/api/user/current-season', { withCredentials: true });
        // Check for offSeasonPlayEnabled in both possible locations
        const offSeasonPlayEnabled = response.data.offSeasonPlayEnabled || 
                                      response.data.season?.offSeasonPlayEnabled || 
                                      false;
        
        // Only show offseason notice if there's no active season AND off-season play is NOT enabled
        if ((!response.data.season || response.data.season.status === 'upcoming') && !offSeasonPlayEnabled) {
          setIsOffseason(true);
          if (response.data.season?.status === 'upcoming') {
            setNextSeason(response.data.season);
          }
        } else {
          setIsOffseason(false);
        }
      } catch (error) {
        console.error('Error checking season:', error);
        setIsOffseason(false);
      } finally {
        setLoading(false);
      }
    };

    checkSeason();
  }, []);

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return null;
  }

  if (!isOffseason) {
    return <>{children}</>;
  }

  return (
    <div className={styles.offseasonContainer}>
      <div className={styles.offseasonNotice}>
        <div className={styles.icon}>⚔️</div>
        <h2 className={styles.title}>Offseason</h2>
        <p className={styles.message}>
          The arena is closed for {feature}. 
          Mercenaries rest and prepare for the next season.
        </p>
        {nextSeason && (
          <div className={styles.nextSeason}>
            <p><strong>{nextSeason.name}</strong> begins on:</p>
            <p className={styles.date}>{formatDate(nextSeason.startDate)}</p>
          </div>
        )}
        <div className={styles.activities}>
          <p>During offseason you can:</p>
          <ul>
            <li>View Hall of Honor rankings</li>
            <li>Review past season statistics</li>
            <li>Browse guild league history</li>
            <li>Wait for the next season to begin</li>
          </ul>
        </div>
      </div>
      {showContentWhenOffseason && children && (
        <div className={styles.disabledContent}>
          {children}
        </div>
      )}
    </div>
  );
};

export default OffseasonNotice;

